import { getExportQuality, SURFACE_POLISH, type ExportQualityId } from "@/config/designer";
import type { VoxelField } from "@/core/field/VoxelField";
import { cloneField, rasterizeFixings, resampleField } from "@/core/field/fieldOps";
import { regularizeAll } from "@/core/field/regularize";
import { buildFixingCutter } from "@/core/fixings/holeSolid";
import type { Fixing } from "@/core/fixings/types";
import {
  computeBounds,
  claySmoothMesh,
  inspectMesh,
  meshVolume,
  weldAndClean,
  type Bounds,
  type ManifoldReport,
} from "@/core/mesh/meshUtils";
import { extractSurfaceNets, type ExtractedMesh } from "@/core/mesh/surfaceNets";
import { subtractSolids } from "./csg";
import { applyGripTexture } from "@/core/finish/gripTexture";
import type { GlossDab } from "@/core/finish/types";

export interface BuildResult {
  mesh: ExtractedMesh;
  report: ManifoldReport;
  bounds: Bounds;
  /** Solid volume, mm^3. */
  volume: number;
  /** True when the fixing holes were cut with the boolean engine. */
  usedCsg: boolean;
  warnings: string[];
  elapsedMs: number;
}

/**
 * Turn the sculpted field plus its fixings into the single closed solid that
 * gets written to STL. This is the one place the export geometry is decided,
 * so tests can exercise exactly what the user downloads.
 */
export async function buildHoldSolid(
  field: VoxelField,
  fixings: readonly Fixing[],
  qualityId: ExportQualityId,
  glossDabs: readonly GlossDab[] = [],
): Promise<BuildResult> {
  const started = Date.now();
  const quality = getExportQuality(qualityId);
  const warnings: string[] = [];

  // Always work on a copy: export regularises the field, and the user's
  // sculpting state must not shift under them when they press Export.
  const working =
    quality.voxelScale === 1 ? cloneField(field) : resampleField(field, field.h * quality.voxelScale);
  regularizeAll(working);

  let mesh = claySmoothMesh(
    weldAndClean(extractSurfaceNets(working)),
    working,
    SURFACE_POLISH.exportIterations,
  );
  let usedCsg = false;

  if (fixings.length > 0) {
    const cutters: ExtractedMesh[] = [];
    for (const fixing of fixings) {
      const cutter = buildFixingCutter(working, fixing, quality.fixingSegments);
      if (cutter) cutters.push(cutter);
      else warnings.push("A fixing sat over empty material and was skipped.");
    }

    if (cutters.length > 0) {
      const csg = await subtractSolids(mesh, cutters);
      if (csg.ok && csg.mesh) {
        // manifold-3d already returns a merged, oriented 2-manifold. Re-welding
        // it by position would fuse vertices that are legitimately distinct at
        // the rim of a hole, so the boolean output is taken as-is.
        mesh = csg.mesh;
        usedCsg = true;
      } else {
        warnings.push(
          `Boolean engine unavailable (${csg.reason ?? "unknown"}); holes cut into the sculpting grid instead.`,
        );
        const rasterField = cloneField(working);
        rasterizeFixings(rasterField, fixings);
        regularizeAll(rasterField);
        mesh = claySmoothMesh(
          weldAndClean(extractSurfaceNets(rasterField)),
          rasterField,
          SURFACE_POLISH.exportIterations,
        );
      }
    }
  }

  // Finish relief comes last so it cannot destabilise the fixing boolean.
  // It only moves existing vertices, so the manifold topology is unchanged.
  mesh = applyGripTexture(mesh, working, glossDabs);

  const report = inspectMesh(mesh);
  if (!report.closed) {
    warnings.push(
      `Mesh check: ${report.boundaryEdges} open edge(s), ${report.nonManifoldEdges} non-manifold edge(s).`,
    );
  }

  return {
    mesh,
    report,
    bounds: computeBounds(mesh.positions),
    volume: meshVolume(mesh),
    usedCsg,
    warnings,
    elapsedMs: Date.now() - started,
  };
}
